export async function formatDuration(
  seconds: number,
  translator: (key: string, params: Record<string, unknown>) => Promise<string>,
  shortFormat: boolean = false,
): Promise<string> {
  const s = Math.max(0, Math.floor(seconds));

  const months = Math.floor(s / 2628000);
  if (months >= 1)
    return await translator("common.duration.long.month", { count: months });

  const weeks = Math.floor(s / 604800);
  if (weeks >= 1)
    return await translator("common.duration.long.week", { count: weeks });

  const days = Math.floor(s / 86400);
  if (days >= 1)
    return await translator("common.duration.long.day", { count: days });

  const hours = Math.floor(s / 3600);
  if (hours >= 1) {
    return shortFormat
      ? await translator("common.duration.short.hour", { count: hours })
      : await translator("common.duration.long.hour", { count: hours });
  }

  const minutes = Math.floor(s / 60);
  if (minutes >= 1) {
    return shortFormat
      ? await translator("common.duration.short.minute", { count: minutes })
      : await translator("common.duration.long.minute", { count: minutes });
  }

  return shortFormat
    ? await translator("common.duration.short.second", { count: s })
    : await translator("common.duration.long.second", { count: s });
}

export function hasDaysPassed(date: Date, days: number): boolean {
  const now = Date.now();
  const diff = now - date.getTime();
  const dayMs = days * 86400000; // 24 * 60 * 60 * 1000

  return diff >= dayMs;
}

function formatDecimal(value: number): string {
  return value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${formatDecimal(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${formatDecimal(bytes / (1024 * 1024))} MB`;
  return `${formatDecimal(bytes / (1024 * 1024 * 1024))} GB`;
}

/**
 * Compact duration format: "45s", "5m 30s", "2h 15m".
 * For human-readable format ("5 minutes"), use `formatDuration`.
 */
export function formatDurationCompact(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    return `${m}m ${s % 60}s`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const formatDate = (
  dateString: number | string | Date | null | undefined,
  locales: Intl.LocalesArgument,
  monthFormat: "numeric" | "2-digit" | "long" | "short" | "narrow" = "short",
): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(locales, {
    year: "numeric",
    month: monthFormat,
    day: "numeric",
  }).format(date);
};

export function formatLocalDateIso(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
